import { useSuspenseQuery } from "@tanstack/react-query";
import { createRoute, type AnyRoute } from "@tanstack/react-router";

import { withRouteRetry } from "@/app/route-retry";
import { loadView, shareViewQuery, viewQuery } from "@/api/views";
import { brandHead, customerHead, NOINDEX_ROBOTS, withRobots } from "@/lib/head";
import ChangeOrderNewPage from "@/pages/app/contracts/[id]/changes/new/page";
import ChangeOrderPage from "@/pages/app/contracts/[id]/changes/[coId]/page";
import ChangeOrderPrintPage from "@/pages/app/contracts/[id]/changes/[coId]/print/page";
import LinkErrorPage from "@/pages/c/[token]/error";
import CustomerContractPage from "@/pages/c/[token]/page";
import CustomerStatementPage from "@/pages/c/[token]/statement/page";

import { appRoute, APP_BOUNDARIES, cRoute } from "./w0b";

const RetryingLinkError = withRouteRetry(LinkErrorPage);

interface NewSearch {
  log?: string;
}

interface StatementSearch {
  by?: string;
}

// 주소의 쿼리는 사람이 고칠 수 있다 — 글자·숫자만 문자열로 받고 나머지는 버린다(값 판정은 core 가 한다).
const textOf = (value: unknown): string | undefined =>
  typeof value === "string" ? value : typeof value === "number" ? String(value) : undefined;

// 고객 링크는 검색엔진에 올리지 않는다.
const customerNoIndexHead = (title: string) => withRobots(customerHead(title), NOINDEX_ROBOTS);

const changeOrderNewQuery = (id: string, search: NewSearch) => viewQuery("change-order-new", { id, ...search });
const changeOrderQuery = (id: string, coId: string) => viewQuery("change-order", { id, coId });
const changeOrderPrintQuery = (id: string, coId: string) => viewQuery("change-order-print", { id, coId });
const customerContractQuery = (token: string) => shareViewQuery("c", token, "customer-contract");
const customerStatementQuery = (token: string, search: StatementSearch) =>
  shareViewQuery("c", token, "customer-statement", search);

const ChangeOrderNewRouteComponent = () => {
  const { id } = changeOrderNewRoute.useParams();
  const search = changeOrderNewRoute.useSearch();
  const { data } = useSuspenseQuery(changeOrderNewQuery(id, search));
  return <ChangeOrderNewPage data={data} />;
};

const changeOrderNewRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/contracts/$id/changes/new",
  validateSearch: (search: Record<string, unknown>): NewSearch => ({ log: textOf(search.log) }),
  loaderDeps: ({ search }) => search,
  loader: ({ context, params, deps }) => loadView(context.queryClient, changeOrderNewQuery(params.id, deps)),
  head: () => brandHead("변경공사 만들기"),
  component: ChangeOrderNewRouteComponent,
  ...APP_BOUNDARIES,
});

const ChangeOrderRouteComponent = () => {
  const { id, coId } = changeOrderRoute.useParams();
  const { data } = useSuspenseQuery(changeOrderQuery(id, coId));
  return <ChangeOrderPage data={data} />;
};

const changeOrderRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/contracts/$id/changes/$coId",
  loader: ({ context, params }) => loadView(context.queryClient, changeOrderQuery(params.id, params.coId)),
  // 제목은 읽어 둔 계약번호·차수·제목 — 없으면 「변경공사」.
  head: ({ match, params }) =>
    brandHead(match.context.queryClient.getQueryData(changeOrderQuery(params.id, params.coId).queryKey)?.title ?? "변경공사"),
  component: ChangeOrderRouteComponent,
  ...APP_BOUNDARIES,
});

const ChangeOrderPrintRouteComponent = () => {
  const { id, coId } = changeOrderPrintRoute.useParams();
  const { data } = useSuspenseQuery(changeOrderPrintQuery(id, coId));
  return <ChangeOrderPrintPage data={data} />;
};

const changeOrderPrintRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/contracts/$id/changes/$coId/print",
  loader: ({ context, params }) => loadView(context.queryClient, changeOrderPrintQuery(params.id, params.coId)),
  head: () => brandHead("변경공사 인쇄"),
  component: ChangeOrderPrintRouteComponent,
  ...APP_BOUNDARIES,
});

const CustomerContractRouteComponent = () => {
  const { token } = customerContractRoute.useParams();
  const { data } = useSuspenseQuery(customerContractQuery(token));
  return <CustomerContractPage data={data} token={token} />;
};

// 고객 계약서 링크 — 오류는 고객용 문구로(내부 메시지를 보이지 않는다).
const customerContractRoute = createRoute({
  getParentRoute: () => cRoute,
  path: "/$token",
  loader: ({ context, params }) => loadView(context.queryClient, customerContractQuery(params.token)),
  head: () => customerNoIndexHead("계약서"),
  component: CustomerContractRouteComponent,
  errorComponent: RetryingLinkError,
});

const CustomerStatementRouteComponent = () => {
  const { token } = customerStatementRoute.useParams();
  const search = customerStatementRoute.useSearch();
  const { data } = useSuspenseQuery(customerStatementQuery(token, search));
  return <CustomerStatementPage data={data} token={token} />;
};

const customerStatementRoute = createRoute({
  getParentRoute: () => cRoute,
  path: "/$token/statement",
  validateSearch: (search: Record<string, unknown>): StatementSearch => ({ by: textOf(search.by) }),
  loaderDeps: ({ search }) => search,
  loader: ({ context, params, deps }) => loadView(context.queryClient, customerStatementQuery(params.token, deps)),
  head: () => customerNoIndexHead("현재 내역서"),
  component: CustomerStatementRouteComponent,
  errorComponent: RetryingLinkError,
});

// g2b 배치의 라우트 — getParentRoute 가 가리키는 부모 아래로 root.tsx 가 꽂는다.
export const routes: AnyRoute[] = [
  changeOrderNewRoute,
  changeOrderRoute,
  changeOrderPrintRoute,
  customerContractRoute,
  customerStatementRoute,
];
